import { useState } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Search, Package, Download, Filter, ChevronLeft, ChevronRight,
  Wrench, FileText, Calendar, AlertTriangle, Plus,
} from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { apiGet } from '@/lib/api'
import { cn } from '@/lib/utils'

interface Asset {
  id: string
  name: string
  code: string
  status: string
  criticality: string
  make: string | null
  model: string | null
  serialNumber: string | null
  purchaseCostGbp: number | null
  condition: string | null
  supplier: string | null
  subCategory: string | null
  category: { id: string; name: string; code: string; icon: string | null } | null
  building: { id: string; name: string; code: string } | null
  floor: { id: string; name: string; number: number } | null
  room: { id: string; name: string; number: string } | null
  _count: { workOrders: number; ppmSchedules: number; documents: number }
}

interface Category {
  id: string
  name: string
  code: string
  children: { id: string; name: string; code: string }[]
}

const CRITICALITY_COLORS: Record<string, string> = {
  CRITICAL: 'bg-red-100 text-red-800',
  HIGH: 'bg-amber-100 text-amber-800',
  MEDIUM: 'bg-blue-100 text-blue-800',
  LOW: 'bg-gray-100 text-gray-700',
}

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  UNDER_REPAIR: 'bg-yellow-100 text-yellow-800',
  DECOMMISSIONED: 'bg-red-50 text-red-600',
  AWAITING_COMMISSIONING: 'bg-blue-100 text-blue-800',
  DISPOSED: 'bg-gray-100 text-gray-500',
}

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Active',
  UNDER_REPAIR: 'Under Repair',
  DECOMMISSIONED: 'Decommissioned',
  AWAITING_COMMISSIONING: 'Awaiting Commission',
  DISPOSED: 'Disposed',
}

export function AssetsPage() {
  const { currentPropertyId } = useAuthStore()
  const pid = currentPropertyId
  const [searchParams, setSearchParams] = useSearchParams()

  const page = parseInt(searchParams.get('page') ?? '1', 10)
  const search = searchParams.get('search') ?? ''
  const categoryId = searchParams.get('categoryId') ?? ''
  const criticality = searchParams.get('criticality') ?? ''
  const status = searchParams.get('status') ?? ''

  const [searchInput, setSearchInput] = useState(search)

  const { data, isLoading } = useQuery({
    queryKey: ['assets', pid, page, search, categoryId, criticality, status],
    queryFn: () =>
      apiGet<{ data: Asset[]; pagination: { page: number; total: number; totalPages: number } }>(
        `/properties/${pid}/assets`,
        {
          page,
          limit: 25,
          ...(search && { search }),
          ...(categoryId && { categoryId }),
          ...(criticality && { criticality }),
          ...(status && { status }),
        }
      ),
    enabled: !!pid,
  })

  const { data: categories } = useQuery({
    queryKey: ['asset-categories'],
    queryFn: () => apiGet<Category[]>(`/properties/${pid}/assets/categories/all`),
    enabled: !!pid,
  })

  function updateParams(updates: Record<string, string>) {
    const params = new URLSearchParams(searchParams)
    Object.entries(updates).forEach(([k, v]) => {
      if (v) params.set(k, v)
      else params.delete(k)
    })
    if (updates.page === undefined) params.set('page', '1')
    setSearchParams(params)
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    updateParams({ search: searchInput })
  }

  function handleQRDownload(assetId: string, assetCode: string) {
    window.open(`/api/v1/properties/${pid}/assets/${assetId}/qr`, '_blank')
  }

  const assets = data?.data ?? []
  const pagination = data?.pagination
  const hasFilters = !!categoryId || !!criticality || !!status

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-800">Assets</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            {pagination ? `${pagination.total} assets` : 'Loading...'}
          </p>
        </div>
        <Link
          to="/assets/new"
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">New Asset</span>
        </Link>
      </div>

      {/* Search & Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-3">
          <form onSubmit={handleSearch} className="flex-1 relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search assets by name, code, serial, make, model..."
              className="input pl-9"
            />
          </form>

          <select
            value={categoryId}
            onChange={(e) => updateParams({ categoryId: e.target.value })}
            className="input w-auto min-w-[160px]"
          >
            <option value="">All Categories</option>
            {categories?.map((cat) => (
              <optgroup key={cat.id} label={cat.name}>
                <option value={cat.id}>{cat.name}</option>
                {cat.children.map((child) => (
                  <option key={child.id} value={child.id}>
                    {child.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>

          <select
            value={criticality}
            onChange={(e) => updateParams({ criticality: e.target.value })}
            className="input w-auto min-w-[140px]"
          >
            <option value="">All Criticality</option>
            <option value="CRITICAL">Critical</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>

          <select
            value={status}
            onChange={(e) => updateParams({ status: e.target.value })}
            className="input w-auto min-w-[140px]"
          >
            <option value="">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="UNDER_REPAIR">Under Repair</option>
            <option value="AWAITING_COMMISSIONING">Awaiting Commission</option>
            <option value="DECOMMISSIONED">Decommissioned</option>
          </select>

          {hasFilters && (
            <button
              onClick={() => updateParams({ categoryId: '', criticality: '', status: '', search: '' })}
              className="btn-secondary text-xs"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-800" />
          </div>
        ) : assets.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No assets found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Asset</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Category</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Location</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500">Value</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Condition</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Status</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-500">WOs</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-500">PPM</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500">QR</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {assets.map((asset) => (
                  <tr key={asset.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <Link to={`/assets/${asset.id}`} className="block">
                        <p className="font-medium text-navy-800 hover:text-navy-600">{asset.name}</p>
                        <p className="text-xs text-gray-400 font-mono">{asset.code}</p>
                        {asset.make && (
                          <p className="text-xs text-gray-400">
                            {asset.make}{asset.model ? ` ${asset.model}` : ''}
                          </p>
                        )}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-600">{asset.category?.name ?? '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      {asset.building ? (
                        <div>
                          <p className="text-gray-600">{asset.building.name}</p>
                          <p className="text-xs text-gray-400">
                            {asset.floor?.name ?? ''}
                            {asset.room ? ` \u2192 Rm ${asset.room.number}` : ''}
                          </p>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {asset.purchaseCostGbp != null ? (
                        <span className="text-navy-800 font-medium">{'\u00a3'}{asset.purchaseCostGbp.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {asset.condition ? (
                        <span className={cn('badge',
                          asset.condition === 'Excellent' ? 'bg-green-100 text-green-800' :
                          asset.condition === 'Good' ? 'bg-blue-100 text-blue-800' :
                          asset.condition === 'Fair' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                        )}>{asset.condition}</span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn('badge', STATUS_COLORS[asset.status])}>
                        {STATUS_LABELS[asset.status] ?? asset.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {asset._count.workOrders > 0 ? (
                        <span className="inline-flex items-center gap-1 text-gray-600">
                          <Wrench className="w-3.5 h-3.5" />
                          {asset._count.workOrders}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {asset._count.ppmSchedules > 0 ? (
                        <span className="inline-flex items-center gap-1 text-gray-600">
                          <Calendar className="w-3.5 h-3.5" />
                          {asset._count.ppmSchedules}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => { e.preventDefault(); handleQRDownload(asset.id, asset.code) }}
                        className="text-gray-400 hover:text-navy-800 transition-colors"
                        title="Download QR Code"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} assets)
            </p>
            <div className="flex gap-1">
              <button
                onClick={() => updateParams({ page: String(page - 1) })}
                disabled={page <= 1}
                className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => updateParams({ page: String(page + 1) })}
                disabled={page >= pagination.totalPages}
                className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
